import { Button, Dialog, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import { AxiosResponse } from 'axios';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';

import {
  GradeSheetsViewModel,
  useCreateAssessment, useDeleteGradeSheet,
  useGetGradeSheets,
  useGetStandards, useUpdateAssessment,
  useUploadFile,
} from '@/api';
import { AssessmentCreate, AssessmentResponse } from '@/api/api-client';
import { SelectFormField, UIDialogTitle } from '@/shared/components';
import { useMedia } from '@/shared/hooks';
import { useAssessmentsContext } from '@/shared/providers';

import { UploadGradeSheets } from './components';
import { AssessmentFormValues, validationSchema } from './duck';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isCreateMode?: boolean;
  setIsCreateMode: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  isCreateMode,
  setIsCreateMode,
}) => {
  const { isSmallScreen } = useMedia();
  const { standards, isStandardsLoading, isStandardsError, refetchStandards } = useGetStandards();
  const { selectedAssessment } = useAssessmentsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { createAssessment, isCreating } = useCreateAssessment();
  const { updateAssessment, isUpdating } = useUpdateAssessment();
  const { uploadFile, isUploading } = useUploadFile();
  const { deleteGradeSheet } = useDeleteGradeSheet();

  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [gradeSheetIdsToDelete, setGradeSheetIdsToDelete] = React.useState<string[]>([]);

  const [originalGradeSheets, setOriginalGradeSheets] = React.useState<GradeSheetsViewModel[]>([]);

  const { gradeSheets, isGradeSheetsLoading } = useGetGradeSheets({
    assessmentId: !isCreateMode && selectedAssessment?.id || '',
  });

  React.useEffect(() => {
    if (gradeSheets && !isCreateMode && selectedAssessment) {
      setOriginalGradeSheets(gradeSheets);
    } else {
      setOriginalGradeSheets([]);
    }
    setGradeSheetIdsToDelete([]);
  }, [gradeSheets, selectedAssessment, isCreateMode]);

  const filteredGradeSheets = React.useMemo(() => {
    return originalGradeSheets.filter(sheet => !gradeSheetIdsToDelete.includes(sheet.id));
  }, [originalGradeSheets, gradeSheetIdsToDelete]);

  const initialFormValues: AssessmentFormValues = React.useMemo(() => {
    return {
      name: !isCreateMode && selectedAssessment?.name || '',
      standard: standards?.find((item) => !isCreateMode && item.id === selectedAssessment?.standardId)?.id ||
                standards?.[0].id || '',
      gradeSheets: filteredGradeSheets?.map(sheet => sheet.id) || [],
    };
  }, [selectedAssessment, standards, filteredGradeSheets, isCreateMode]);

  const handleClose = () => {
    setUploadedFiles([]);
    setGradeSheetIdsToDelete([]);
    if (isCreateMode) {
      setIsCreateMode(false);
    }
    onClose();
  };

  const handleMarkForDeletion = (fileId: string) => {
    if (!gradeSheetIdsToDelete.includes(fileId)) {
      setGradeSheetIdsToDelete(prev => [...prev, fileId]);
    }
  };

  const handleSubmit = async (values: AssessmentFormValues, { resetForm }: FormikHelpers<AssessmentFormValues>) => {
    setIsSubmitting(true);

    try {
      const assessmentData: AssessmentCreate = {
        name: values.name,
        standard: values.standard,
      };

      let response: AxiosResponse<AssessmentResponse>;

      try {
        if (!isCreateMode && selectedAssessment) {
          response = await updateAssessment({
            assessment_id: selectedAssessment.id,
            ...assessmentData,
          });

          if (response?.data?.id && gradeSheetIdsToDelete.length > 0) {
            try {
              const deletePromises = gradeSheetIdsToDelete.map(fileId => deleteGradeSheet(fileId));
              await Promise.all(deletePromises);
            } catch (deleteError) {
              enqueueSnackbar({
                message: `Some files failed to delete${deleteError instanceof Error ? ': ' + deleteError.message : ''}`,
                variant: 'warning',
              });
            }
          }
        } else {
          response = await createAssessment(assessmentData);
        }
      } catch (error) {
        const actionType = isCreateMode ? 'creating' : 'updating';
        enqueueSnackbar({
          message: `An error occurred while ${actionType} the assessment${error instanceof Error
            ? ': ' + error.message
            : ''}`,
          variant: 'error',
        });
        setIsSubmitting(false);
        return;
      }

      if (uploadedFiles.length > 0) {
        try {
          const uploadPromises = uploadedFiles.map(file => {
            return uploadFile({
              file,
              assessmentId: response.data.id,
              standardId: response.data.standard,
              newAssessmentName: response.data.name,
            });
          });

          await Promise.all(uploadPromises);
        } catch (uploadError) {
          enqueueSnackbar({
            message: `File upload failed${uploadError instanceof Error ? ': ' + uploadError.message : ''}`,
            variant: 'warning',
          });
          // Continue with assessment creation even if file upload fails
        }
      }

      enqueueSnackbar({
        message: isCreateMode ? 'Assessment created successfully!' : 'Assessment updated successfully!',
        variant: 'success',
      });

      resetForm();
      handleClose();
    } catch (error) {
      enqueueSnackbar({
        message: `An unexpected error occurred${error instanceof Error ? ': ' + error.message : ''}`,
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilesChange = (files: File[]) => {
    setUploadedFiles(files);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullScreen={isSmallScreen}
      fullWidth
      maxWidth='md'
      scroll='paper'
      aria-labelledby='dialog-title'
      aria-describedby='dialog-content'
    >
      <UIDialogTitle
        id='dialog-title'
        title={isCreateMode ? 'New Assessment' : 'Edit Assessment'}
        onCloseBtnAction={handleClose}
      />

      <Formik<AssessmentFormValues>
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        enableReinitialize
      >
        {({ isSubmitting: formikIsSubmitting, errors, touched, resetForm }) => {
          const isFormDisabled = isGradeSheetsLoading || isSubmitting || formikIsSubmitting || isCreating ||
                                 isUploading || isUpdating;

          return (
            <Form>
              <DialogContent id='dialog-content'>
                <Stack
                  gap='2rem'
                  flexDirection={isSmallScreen ? 'column' : 'row'}
                  justifyContent='space-between'
                  width='100%'
                >
                  <Field
                    as={TextField}
                    name='name'
                    required
                    fullWidth
                    type='text'
                    label='Assessment name'
                    variant='outlined'
                    helperText={<ErrorMessage name='name' />}
                    error={Boolean(touched.name && errors.name)}
                    disabled={isFormDisabled}
                  />

                  <SelectFormField
                    required
                    name='standard'
                    options={standards}
                    fullWidth
                    label='Standard'
                    variant='outlined'
                    helperText={
                      touched.standard && errors.standard ? (
                        <ErrorMessage name='standard' />
                      ) : isStandardsError ? (
                        'Failed to load standards'
                      ) : ''
                    }
                    disabled={isStandardsError || isFormDisabled}
                    error={Boolean(touched.standard && errors.standard) || isFormDisabled}
                    isLoading={isStandardsLoading}
                    isErrorIcon={isStandardsError}
                    onErrorIconAction={refetchStandards}
                  />
                </Stack>

                <UploadGradeSheets
                  multiple
                  onDrop={handleFilesChange}
                  disabled={isFormDisabled}
                  gradeSheets={filteredGradeSheets}
                  onMarkForDeletion={handleMarkForDeletion}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                  disabled={isFormDisabled}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  type='submit'
                  disabled={isFormDisabled}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CreateEditModal;